import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { useCitation } from '@vivli/shared/features/citations/infrastructure/hook';
import { Color, Size } from '@vivli/shared/theme';
import { CSSProperties } from 'react';
import Tippy from '@tippyjs/react';
import { useToastApiContext } from '@vivli/core/infrastructure/context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

const citationStyleGeneric: CSSProperties = {
    fontSize: Size.FontSize.Medium,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    flexWrap: 'wrap',
    color: Color.DARK_GRAY,
    marginRight: '10px',
    backgroundColor: Color.TRANSPARENT,
    marginBottom: '-20px',
};

const citationBodyStyle: CSSProperties = {
    ...citationStyleGeneric,
    marginTop: '-5px',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
};

const citationJournalStyle: CSSProperties = {
    ...citationStyleGeneric,
    fontStyle: 'italic',
    marginRight: '0px',
};

const citationTitleStyle: CSSProperties = {
    ...citationStyleGeneric,
    marginRight: '0px',
    minWidth: '0px',
    maxWidth: '100%',
    whiteSpace: 'normal',
    lineHeight: '13px',
    marginTop: '10px',
    marginBottom: '10px',
};

const iconStyle: CSSProperties = {
    fontSize: '1.5em',
    padding: '0px 0px 0px 0px',
    color: Color.TEXT_LIGHT_BLUE,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    cursor: 'pointer',
};

const twoColumnGrid: CSSProperties = {
    width: '100%',
    display: 'grid',
    justifyContent: 'right',
    gridTemplateColumns: '16fr 1fr',
    columnGap: '10px',
};

interface CitationComponentProps {
    citation: ICitation;
}

/* not in use - replaced by citation-view.tsx - but left here for reference JUST IN CASE*/
export const CitationComponent = ({ citation }: CitationComponentProps) => {
    const { formattedAuthors, remainderFormatted, formattedDoi } = useCitation(citation);
    const formattedAuthorsString = formattedAuthors(false);
    const remainderFormattedString = remainderFormatted(false);
    const formattedDoiString = formattedDoi();

    const titleText = citation ? `"${citation?.title}".` : '-';
    const journalText = citation?.journal ? `${citation?.journal},` : '-';
    const noDoi = formattedDoiString === '-';

    const entireCitation = `${formattedAuthorsString} ${titleText} ${journalText}  ${remainderFormattedString} ${formattedDoiString}`;

    const toastService = useToastApiContext();

    const handleCopyToClipboard = (value) => {
        navigator.clipboard
            .writeText(value)
            .then(() => toastService.success('Copied!'))
            .catch(() => toastService.error('Failed to copy:'));
    };

    return (
        <div style={twoColumnGrid}>
            <div style={{ overflowX: 'auto' }}>
                <Tippy content={formattedAuthorsString}>
                    <div style={citationBodyStyle}>{formattedAuthorsString}</div>
                </Tippy>
                <div style={citationTitleStyle}>{titleText}</div>
                <div style={{ marginTop: '-20px' }}>
                    <span style={citationJournalStyle}>{journalText}</span>
                    <span style={citationStyleGeneric}>
                        {remainderFormattedString}
                        {!noDoi && (
                            <a href={formattedDoiString} target="_blank">
                                doi: {formattedDoiString}
                            </a>
                        )}
                        {noDoi && (
                            <a href={citation?.url} target="_blank">
                                url: {citation?.url}
                            </a>
                        )}
                    </span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <FontAwesomeIcon onClick={() => handleCopyToClipboard(entireCitation)} style={iconStyle} icon={faCopy} />
            </div>
        </div>
    );
};
